/**
 * Challenge Checks — daily, one row per assigned challenge.
 *
 * Each challenge gets its own three-state row (Yes / No / N/A) per day. N/A
 * is per-challenge ("no progress on THIS challenge today") so a tester
 * who progressed on one challenge but not another can mark them
 * appropriately. Past days where any challenge is missing get stacked above.
 */

import { format, addDays } from "date-fns";
import type { Tester, Verification, VerificationStatus } from "../../types";
import { Section, CheckRow, StaleDayHeader } from "../Section";
import { YesNoNaButtons } from "../VerificationButtons";
import { challengeProgressKey } from "../../lib/conditionKeys";
import { openAsanaBugForm } from "../../lib/asana";

interface ChallengeChecksSectionProps {
  profile: Tester;
  currentDayNum: number;
  cohortStart: Date;
  asanaUrl: string;
  getStatus: (key: string, day: number) => Verification | null;
  setStatus: (input: {
    conditionKey: string;
    testingDay: number;
    status: VerificationStatus;
  }) => Promise<void>;
}

export function ChallengeChecksSection({
  profile,
  currentDayNum,
  cohortStart,
  asanaUrl,
  getStatus,
  setStatus,
}: ChallengeChecksSectionProps) {
  if (profile.challenges.length === 0) return null;

  const challenges = profile.challenges.map((c) => ({
    name: c,
    key: challengeProgressKey(c),
  }));

  // Past days where any challenge is missing.
  const pastDays: number[] = [];
  for (let d = 1; d < currentDayNum; d++) {
    const anyMissing = challenges.some((c) => !getStatus(c.key, d));
    if (anyMissing) pastDays.push(d);
  }

  const todayDate = format(addDays(cohortStart, currentDayNum - 1), "EEE, MMM d");

  const handleSelect = async (
    testingDay: number,
    challenge: { name: string; key: string },
    status: VerificationStatus,
  ) => {
    await setStatus({ conditionKey: challenge.key, testingDay, status });
    if (status === "issue_found") {
      const dayDate = format(addDays(cohortStart, testingDay - 1), "EEE, MMM d");
      const dayLabel = `Day ${testingDay} of ${
        profile.tester_type === "Volunteer" ? "14" : "7"
      }`;
      openAsanaBugForm(asanaUrl, {
        email: profile.email,
        wordpressUserId: profile.wordpress_user_id,
        dayLabel,
        dayDate,
        conditionLabel: `Challenge: ${challenge.name} (Day ${testingDay} - ${dayDate})`,
      });
    }
  };

  const renderDay = (testingDay: number) =>
    challenges.map((c) => {
      const row = getStatus(c.key, testingDay);
      return (
        <CheckRow
          key={c.key}
          label={
            <>
              Does progress for <strong>{c.name}</strong> look correct?
            </>
          }
          flagged={row?.status === "issue_found"}
          trailing={
            <YesNoNaButtons
              status={row?.status ?? null}
              onSelect={(s) => handleSelect(testingDay, c, s)}
            />
          }
        />
      );
    });

  return (
    <Section title="Challenge checks" meta={`Today · ${todayDate}`} accent="active">
      {pastDays.map((d) => {
        const date = format(addDays(cohortStart, d - 1), "EEE, MMM d");
        return (
          <div key={d}>
            <StaleDayHeader label={`Day ${d} · ${date} (overdue)`} />
            {renderDay(d)}
          </div>
        );
      })}

      {pastDays.length > 0 && <StaleDayHeader label={`Today · ${todayDate}`} />}

      {renderDay(currentDayNum)}

      <div className="px-3 py-2 text-[10px] text-zinc-400 italic border-t border-zinc-100">
        N/A means you didn't have any progress on that challenge that day.
      </div>
    </Section>
  );
}
